import React, { useCallback, useState, useRef, useEffect } from 'react';
import { colors } from '../../styles/theme';
import { PRIORITY, ROOT_NODE } from '../../utils/constants';
import { Priority } from '../../types/todo';
import { getPriorityColor } from '../../utils/priority';

interface TodoNodeActionsProps {
  id: string;
  priority: Priority;
  onPriorityChange: (priority: Priority) => void;
  onDropdownOpenChange?: (isOpen: boolean) => void;
}

/**
 * Expanded-only node details. Currently just the priority badge/dropdown
 * (the time estimate and add-child/archive actions live in the header's
 * right cluster so they stay visible in the compact row).
 */
const TodoNodeActions: React.FC<TodoNodeActionsProps> = ({
  id,
  priority,
  onPriorityChange,
  onDropdownOpenChange,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isRootNode = id === ROOT_NODE.UUID;

  const priorities: Priority[] = ['low', 'medium', 'high', 'critical'];

  const handlePriorityClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isRootNode) {
      setShowDropdown(prev => {
        const newValue = !prev;
        onDropdownOpenChange?.(newValue);
        return newValue;
      });
    }
  }, [isRootNode, onDropdownOpenChange]);

  const handlePrioritySelect = useCallback((selectedPriority: Priority) => {
    onPriorityChange(selectedPriority);
    setShowDropdown(false);
    onDropdownOpenChange?.(false);
  }, [onPriorityChange, onDropdownOpenChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        onDropdownOpenChange?.(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown, onDropdownOpenChange]);

  return (
    <div className="node-details">
      <div className="priority-dropdown-container" ref={dropdownRef}>
        <button
          className="priority-badge"
          style={{
            backgroundColor: getPriorityColor(priority),
            color: colors.text.inverted,
            cursor: isRootNode ? 'not-allowed' : 'pointer',
            opacity: isRootNode ? 0.6 : 1,
            border: 'none',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '11px',
            fontWeight: 'bold'
          }}
          title={isRootNode ? 'Priority locked for root node' : `Priority: ${priority} (click to change)`}
          onClick={handlePriorityClick}
          disabled={isRootNode}
        >
          {priority}
        </button>

        {showDropdown && !isRootNode && (
          <div className="priority-dropdown">
            {priorities.map((prio) => (
              <button
                key={prio}
                className="priority-option"
                style={{
                  backgroundColor: PRIORITY.COLORS[prio],
                  color: colors.text.inverted
                }}
                onClick={() => handlePrioritySelect(prio)}
              >
                {prio}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TodoNodeActions;
